import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "stories/components";
import { Input } from "stories/forms";
import { Row, Col } from "stories/layout";
import FileDownloadButton from "components/Buttons/FileDownloadButton/FileDownloadButton";
import { httpGetFairValues } from "services/fund_fair_values";

import { formatAmount } from "views/app/FundValuationReportEdit/fundValuationPayload";
import { EMPTY_CELL, formatDay, sourceLabel } from "views/app/PortfolioSummary/fairValues";
import { buildCalculation, defaultFairValueDate, normalizeBreakdown, transactionGroup } from "./fundFairValueCalc";

import "./FundFairValueCalculation.css";

// The fair value of one fund on day D per portfolio, in the IFRS and the live view, with
// the report and the transactions behind it (server docs/fund-fair-value.md, breakdown).
// Read only: every member of the account sees the same.

const amount = (value) => (value === null || value === undefined ? EMPTY_CELL : formatAmount(value) || EMPTY_CELL);

const groupLabel = (group, t) => {
  switch (group) {
    case "calls":
      return t("fair_value_calc_calls", "Kutsut");
    case "capitalized_interest":
      return t("fair_value_calc_capitalized_interest", "Pääomitettu korko");
    case "returns":
      return t("fair_value_calc_returns", "Palautukset");
    case "distributions":
      return t("fair_value_calc_distributions", "Jaot");
    default:
      return EMPTY_CELL;
  }
};

const signText = (sign) => (sign > 0 ? "+" : sign < 0 ? "−" : "");

const Line = ({ label, value, className = "" }) => (
  <tr className={className}>
    <th scope="row" className="fund-fair-value__label">{label}</th>
    <td className="text-right">{value}</td>
  </tr>
);

const ReportView = ({ view, date, t }) => {
  const report = view.report;
  const calc = buildCalculation(view);
  const transactions = view.transactions || [];
  return (
    <>
      <table className="fund-fair-value__facts">
        <tbody>
          <Line label={t("fair_value_calc_period", "Jakso")} value={report.report_period} />
          <Line label={t("fair_value_calc_value_date", "Arvopäivä")} value={formatDay(report.value_date)} />
          <Line label={t("fair_value_calc_received", "Saapunut")} value={formatDay(report.report_received)} />
          <Line label={t("fair_value_calc_ifrs_date", "IFRS-päivä")} value={formatDay(report.ifrs_date)} />
          {report.attachment_name ? (
            <Line
              label={t("fair_value_calc_attachment", "Liite")}
              value={
                <FileDownloadButton showPreview fileModel="FundValuationReport" fileId={report.id} fileName={report.attachment_name} />
              }
            />
          ) : null}
        </tbody>
      </table>

      <table className="fund-fair-value__calc">
        <tbody>
          <Line label={t("fair_value_calc_report_value", "Raportin arvo")} value={amount(calc.report_value)} className="fund-fair-value__line--report" />
          <Line label={`+ ${groupLabel("calls", t)}`} value={amount(calc.calls)} className="fund-fair-value__line--calls" />
          {calc.show_capitalized_interest ? (
            <Line
              label={`+ ${groupLabel("capitalized_interest", t)}`}
              value={amount(calc.capitalized_interest)}
              className="fund-fair-value__line--capitalized-interest"
            />
          ) : null}
          <Line label={`− ${groupLabel("returns", t)}`} value={amount(calc.returns)} className="fund-fair-value__line--returns" />
          <Line label={`− ${groupLabel("distributions", t)}`} value={amount(calc.distributions)} className="fund-fair-value__line--distributions" />
          <Line
            label={`= ${t("fair_value_calc_fair_value", "Käypä arvo {{date}}", { date: formatDay(date) })}`}
            value={<strong>{amount(calc.fair_value)}</strong>}
            className="fund-fair-value__line--fair-value"
          />
        </tbody>
      </table>

      <details className="fund-fair-value__transactions">
        <summary>{t("fair_value_calc_transactions", "Tapahtumat raportin jälkeen ({{n}})", { n: transactions.length })}</summary>
        {transactions.length === 0 ? (
          <Typography className="text-muted">{t("fair_value_calc_no_transactions", "Ei tapahtumia")}</Typography>
        ) : (
          <table className="table table-sm">
            <tbody>
              {transactions.map((transaction, index) => (
                <tr key={transaction.id ?? index} className="fund-fair-value__transaction">
                  <td>{formatDay(transaction.date)}</td>
                  <td>{groupLabel(transactionGroup(transaction), t)}</td>
                  <td className="text-right">
                    {signText(transaction.sign)}
                    {amount(
                      transaction.amount_converted === null
                        ? transaction.amount === null ? null : Math.abs(transaction.amount)
                        : Math.abs(transaction.amount_converted)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </>
  );
};

const NoReportView = ({ view, t }) => (
  <table className="fund-fair-value__calc">
    <tbody>
      <Line label={t("fair_value_calc_fair_value_short", "Käypä arvo")} value={<strong>{amount(view.fair_value)}</strong>} className="fund-fair-value__line--fair-value" />
      <Line label={t("fair_value_calc_source", "Lähde")} value={sourceLabel(view.source, t)} className="fund-fair-value__line--source" />
    </tbody>
  </table>
);

const ViewColumn = ({ name, view, date, t }) => (
  <Col md="6" className={`fund-fair-value__view fund-fair-value__view--${name}`}>
    <h4 className="fund-fair-value__view-title">
      {name === "ifrs"
        ? t("fair_value_calc_view_ifrs", "IFRS (tilikausi)")
        : t("fair_value_calc_view_live", "Live (uusin raportti)")}
    </h4>
    {!view ? (
      <Typography className="text-muted">{EMPTY_CELL}</Typography>
    ) : view.report ? (
      <ReportView view={view} date={date} t={t} />
    ) : (
      <NoReportView view={view} t={t} />
    )}
  </Col>
);

const FundFairValueCalculation = ({ fundId, portfolio }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(() => defaultFairValueDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fundId || !date) {
      return undefined;
    }
    // A late response for an older day or portfolio is dropped.
    let active = true;
    const params = { private_investment_fund: fundId, date };
    if (portfolio) {
      params.portfolio = portfolio;
    }
    setLoading(true);
    setError(false);
    httpGetFairValues(params)
      .then(
        (res) => {
          if (active) {
            setData(normalizeBreakdown(res?.data));
          }
        },
        (err) => {
          if (active) {
            setData(null);
            // api.js already tells about a 403.
            setError(err?.status !== 403);
          }
        }
      )
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [fundId, portfolio, date]);

  const portfolios = data?.portfolios || [];

  return (
    <section className="fund-fair-value mb-4">
      <div className="d-flex flex-wrap align-items-end mb-2">
        <h3 className="fund-fair-value__title mr-3 mb-2">{t("fair_value_calc_title", "Käyvän arvon laskelma")}</h3>
        <div style={{ minWidth: "200px" }}>
          <Input
            type="date"
            name="fund_fair_value_date"
            label={t("fair_value_calc_date", "Päivä")}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      {error ? (
        <Typography className="text-danger mb-2" role="alert">
          {t("fair_value_calc_error", "Laskelmaa ei saatu haettua")}
        </Typography>
      ) : null}

      {!error && !loading && data && portfolios.length === 0 ? (
        <Typography className="text-muted mb-2">
          {t("fair_value_calc_empty", "Rahastoa ei ole salkuissa valittuna päivänä")}
        </Typography>
      ) : null}

      {portfolios.map((p, index) => (
        <div key={p.portfolio ?? index} className="fund-fair-value__portfolio card mb-3">
          <div className="card-body">
            <h4 className="fund-fair-value__portfolio-name">{p.portfolio_name || EMPTY_CELL}</h4>
            <Row>
              <ViewColumn name="ifrs" view={p.views.ifrs} date={date} t={t} />
              <ViewColumn name="live" view={p.views.live} date={date} t={t} />
            </Row>
          </div>
        </div>
      ))}
    </section>
  );
};

export default FundFairValueCalculation;
